import { useRef, useState, useEffect } from "react";
import { Formik, Field, Form } from "formik";
import {
  InputGroup,
  FieldWrappers,
  Label,
  SubmitBtn,
  SideCancelBtn,
  CardWrapper,
  SelectionCard,
  Wrapper,
  ScrollableContent,
  ActionSection,
  RupeeIcon,
  MobileIcon,
  ModalHeader,
} from "../Styles/InputStyles";
import { toast } from "react-toastify";
import tw from "tailwind-styled-components";
import { MdClose, MdEdit } from "react-icons/md";
import * as Yup from "yup";
import Config, { staff_roles } from "../../Config";
import CategoryCard from "../CategoryCard";
import MultiPropSelectModal from "../MultiPropSelectModal";
import SelectOptionModal from "../SelectOptionModal";
import { FormControl, MenuItem, Select } from "@mui/material";
import { useSelector } from "react-redux";

const AddStaff = ({
  addStaffLoading,
  addStaffMutate,
  setIsAddStaff,
  property,
  key,
}) => {
  const user = useSelector((state) => state.UserReducer.user);
  const filteredProperties = property?.filter((prop) => prop.id !== 0) || [];
  const [height, setHeight] = useState(window.innerHeight);
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  let formikRef = useRef(null);

  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.resetForm();
    }
    const handleResize = () => {
      setHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [key]);

  const initialValues = {
    fullName: "",
    mobile: "",
    staff: "",
    gender: "",
    salary: "",
    properties: [],
    commissionType: "",
    commissionValue: "",
    isOnPayroll: 1,
    isSuperAdmin: 0,
  };

  const gender = [
    { value: 1, label: "Male" },
    { value: 2, label: "Female" },
  ];

  const staffRoleOptions = staff_roles.map((role) => ({
    label: role.label,
    value: role.value,
  }));

  const isSalesHead = user.role === Config.STAFF_ROLES.SALES_HEAD;

  const validationSchema = Yup.object({
    fullName: Yup.string()
      .required("Name is required")
      .matches(
        /^[A-Za-z0-9.\s]+$/,
        "Only letters, numbers, spaces and dot are allowed."
      )
      .min(4, "Name must be at least 4 characters long"),
    mobile: Yup.string()
      .required("Mobile number is required")
      .matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits"),
    gender: Yup.number().required("Gender is required"),
    // Staff role is only required for non-Sales Head users
    staff: Yup.number()
      .when([], {
        is: () => !isSalesHead,
        then: (schema) => schema.required("Staff Role is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    properties: Yup.array()
      .min(1, "At least one property is required")
      .required("Properties are required"),
    commissionType: Yup.string(),
    commissionValue: Yup.mixed().when("commissionType", {
      is: (val) => val !== null && val !== undefined && val !== "",
      then: (schema) => {
        return schema
          .required("Commission value is required")
          .test(
            "is-positive-number",
            "Commission value must be a positive number",
            (value) => {
              if (value === "" || value === null || value === undefined) return false;
              const numValue = Number(value);
              return !isNaN(numValue) && numValue > 0;
            }
          )
          .test(
            "flat-amount-range",
            "Flat commission cannot exceed ₹5,00,000",
            function (value) {
              const { commissionType } = this.parent;
              if (commissionType !== 0) return true;
              if (value === "" || value === null || value === undefined) return false;
              const numValue = Number(value);
              return numValue <= 500000;
            }
          )
          .test(
            "percentage-range",
            "Percentage must be between 1% and 100%",
            function (value) {
              const { commissionType } = this.parent;
              if (commissionType !== 1) return true;
              if (value === "" || value === null || value === undefined) return false;
              const numValue = Number(value);
              return numValue >= 1 && numValue <= 100;
            }
          );
      },
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const handleSubmit = (values, { resetForm }) => {
    const nameRegex = /^[A-Za-z0-9. ]{4,}$/;
    const mobileRegex = /^\d{10}$/;
    const salaryRegex = /^\d{1,7}$/;
    const maxSalaryDeposit = 1000000;

    if (!nameRegex.test(values.fullName)) {
      toast.error("Only letters, numbers, spaces and dot are allowed.");
      return;
    }

    if (!mobileRegex.test(values.mobile)) {
      toast.error(
        "Mobile number should be exactly 10 digits long and contain only numbers"
      );
      return;
    }

    if (!values.gender) {
      toast.error("Gender is required");
      return;
    }

    // Only validate staff role for non-Sales Head users
    if (!isSalesHead && !values.staff) {
      toast.error("Staff Role is required");
      return;
    }

    if (!values.properties || values.properties.length === 0) {
      toast.error("At least one property is required");
      return;
    }
    if (
      (values.salary && !salaryRegex.test(values.salary)) ||
      parseInt(values.salary) > maxSalaryDeposit
    ) {
      toast.error(
        "Salary must be less than 10,00,000 and contain only numbers"
      );
      return;
    }

    // Base payload with required fields
    const payload = {
      name: values.fullName,
      mobile: values.mobile,
      role: isSalesHead ? Config.STAFF_ROLES.SALESPERSON : values.staff,
      gender: values.gender,
      salary: Number(values.salary) || 0,
      properties: values.properties,
      commissionType: values.commissionType,
      commission: values.commissionValue,
      isOnPayroll: values.isOnPayroll,
      isSuperAdmin: values?.isSuperAdmin || 0
    };
    addStaffMutate(payload);
  };

  const handleSelectedProperties = (selectedProperties) => {
    if (formikRef.current) {
      formikRef.current.setFieldValue("properties", selectedProperties);
    }
  };

  const handlePropertySave = (properties) => {
    handleSelectedProperties(properties);
    setShowPropertyModal(false);
  };

  const handleIncrementTypeChange = (type, setFieldValue) => {
    setFieldValue("commissionType", type);
    setFieldValue("commissionValue", "");
    // Trigger validation
    setTimeout(() => {
      setFieldValue("commissionValue", "", true);
    }, 0);
  };

  const handleIncrementValueChange = (value, setFieldValue) => {
    setFieldValue("commissionValue", value, true);
  };

  const renderSelectedProperties = (selected = []) => {
    if (!selected.length) return "Select Properties";
    const selectedProperties = filteredProperties?.filter((prop) =>
      selected.includes(prop.id)
    );
    if (!selectedProperties?.length) return "Select Properties";
    const maxToShow = 2;
    const namesToShow = selectedProperties
      .slice(0, maxToShow)
      .map((prop) => prop.name);
    const remainingCount = selectedProperties.length - maxToShow;
    return (
      <div className="flex items-center gap-1 truncate">
        {namesToShow.join(", ")}
        {remainingCount > 0 && (
          <span className="ml-1 text-gray-500">+{remainingCount}</span>
        )}
      </div>
    );
  };

  const handlePayrollToggle = (event, setFieldValue) => {
    setFieldValue("isOnPayroll", event.target.checked ? 1 : 2);
  };
  const handleIsSuperAdminToggle = (event, setFieldValue) => {
    setFieldValue("isSuperAdmin", event.target.checked ? 1 : 0);
  };

  return (
    <Container
      style={{
        height: `${height}px`,
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <ModalHeader>
        <h1 className="text-white text-lg font-medium">Add Staff</h1>
        <button onClick={() => setIsAddStaff(false)}>
          <MdClose color="white" size={24} />
        </button>
      </ModalHeader>

      <div className="px-6 h-full flex flex-col justify-between">
        <Formik
          initialValues={initialValues}
          innerRef={formikRef}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnChange={true}
          validateOnBlur={true}
        >
          {({ values, setFieldValue, resetForm, errors, touched }) => {
            const getCurrentRoleLabel = () => {
              const selectedRole = staff_roles.find(
                (role) => role.value === values?.staff
              );
              return selectedRole ? selectedRole.label : "Select staff role";
            };

            const handleRoleSelect = (selectedValue) => {
              setFieldValue("staff", selectedValue);
            };

            return (
              <Form>
                <ScrollableContent className="max-h-[calc(100vh-120px)]">
                  <Wrapper>
                    <InputGroup>
                      <Label htmlFor="fullName">Name *</Label>
                      <FieldWrappers>
                        <Field
                          type="text"
                          name="fullName"
                          id="fullName"
                          autoComplete="off"
                        />
                      </FieldWrappers>
                      {errors.fullName && touched.fullName && (
                        <ErrorText>{errors.fullName}</ErrorText>
                      )}
                    </InputGroup>
                    <InputGroup>
                      <Label htmlFor="mobile">Mobile *</Label>
                      <FieldWrappers>
                        <MobileIcon />
                        <Field
                          type="text"
                          name="mobile"
                          id="mobile"
                          inputMode="numeric"
                          autoComplete="off"
                          className="pl-5"
                          maxLength={10}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d{0,10}$/.test(value)) {
                              setFieldValue("mobile", value, true);
                            }
                          }}
                        />
                      </FieldWrappers>
                      {errors.mobile && touched.mobile && (
                        <ErrorText>{errors.mobile}</ErrorText>
                      )}
                    </InputGroup>

                    <InputGroup>
                      <Label>Gender *</Label>
                      <CardWrapper>
                        {gender.map((mode) => (
                          <SelectionCard
                            key={mode.value}
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              setFieldValue("gender", mode.value, true);
                            }}
                            className={
                              values.gender === mode.value
                                ? "bg-[#0078BD] text-white"
                                : "bg-gray-100 text-gray-600"
                            }
                          >
                            <span className="text-sm">{mode.label}</span>
                          </SelectionCard>
                        ))}
                      </CardWrapper>
                      {errors.gender && touched.gender && (
                        <ErrorText>{errors.gender}</ErrorText>
                      )}
                    </InputGroup>

                    {/* Staff Role - Only show for non-Sales Head users */}
                    {!isSalesHead && (
                      <InputGroup>
                        <Label htmlFor="staff">Staff Role *</Label>
                        <FieldWrappers onClick={() => setIsRoleModalOpen(true)}>
                          <Field
                            type="text"
                            name="staffDisplay"
                            id="staffDisplay"
                            autoComplete="off"
                            readOnly
                            value={getCurrentRoleLabel()}
                            className="w-full border-none outline-none bg-transparent cursor-pointer"
                          />
                          <MdEdit className="w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex-shrink-0 mr-2" />
                        </FieldWrappers>
                        {errors.staff && touched.staff && (
                          <ErrorText>{errors.staff}</ErrorText>
                        )}
                      </InputGroup>
                    )}

                    {/* For Sales Head, show a read-only field with "Salesperson" */}
                    {isSalesHead && (
                      <InputGroup>
                        <Label htmlFor="staff">Staff Role</Label>
                        <FieldWrappers>
                          <Field
                            type="text"
                            name="staffDisplay"
                            id="staffDisplay"
                            autoComplete="off"
                            readOnly
                            value="Sales person"
                            className="w-full border-none outline-none bg-transparent cursor-not-allowed opacity-60"
                            disabled
                          />
                        </FieldWrappers>
                        <p className="text-xs text-gray-400 mt-1">
                          Role automatically set to Salesperson
                        </p>
                      </InputGroup>
                    )}

                    <InputGroup>
                      <Label>Select property/properties *</Label>
                      <CategoryCard
                        type="button"
                        selectedCategory={
                          values.properties.length > 0
                            ? {
                              label: renderSelectedProperties(
                                values.properties
                              ),
                              value: "properties",
                            }
                            : null
                        }
                        title="Property/or Properties"
                        onClick={() => setShowPropertyModal(true)}
                      />
                      {errors.properties && touched.properties && (
                        <ErrorText>{errors.properties}</ErrorText>
                      )}
                    </InputGroup>

                    <InputGroup>
                      <PayrollContainer>
                        <Label>Employment Type</Label>

                        <div className="flex bg-gray-100 rounded-xl p-1 w-fit">
                          <button
                            type="button"
                            onClick={() => setFieldValue("isOnPayroll", 1)}
                            className={`px-4 py-1.5 text-sm rounded-md hover:text-gray-900  transition-all ${values.isOnPayroll === 1
                              ? "bg-white shadow text-gray-900"
                              : "text-gray-500"
                              }`}
                          >
                            Payroll
                          </button>

                          <button
                            type="button"
                            onClick={() => setFieldValue("isOnPayroll", 2)}
                            className={`px-4 py-1.5 text-sm rounded-md hover:text-gray-900 transition-all ${values.isOnPayroll === 2
                              ? "bg-white shadow text-gray-900"
                              : "text-gray-500"
                              }`}
                          >
                            Contract
                          </button>
                        </div>
                      </PayrollContainer>
                    </InputGroup>

                    <InputGroup>
                      <Label htmlFor="salary">Monthly Salary </Label>
                      <FieldWrappers>
                        <RupeeIcon />
                        <Field
                          type="number"
                          name="salary"
                          id="salary"
                          autoComplete="off"
                          className="w-full pl-5"
                          min="0"
                          max="1000000"
                        />
                      </FieldWrappers>
                    </InputGroup>

                    {values.staff === Config.STAFF_ROLES.ADMIN && (
                      <InputGroup>
                        <PayrollContainer>
                          <Label>Super Admin</Label>

                          <div className="flex bg-gray-100 rounded-xl p-1 w-fit">
                            <button
                              type="button"
                              onClick={() => setFieldValue("isSuperAdmin", 0)}
                              className={`px-4 py-1.5 text-sm rounded-md transition-all ${values.isSuperAdmin === 0
                                  ? "bg-white shadow text-gray-900"
                                  : "text-gray-500"
                                }`}
                            >
                              No
                            </button>

                            <button
                              type="button"
                              onClick={() => setFieldValue("isSuperAdmin", 1)}
                              className={`px-4 py-1.5 text-sm rounded-md transition-all ${values.isSuperAdmin === 1
                                  ? "bg-white shadow text-gray-900"
                                  : "text-gray-500"
                                }`}
                            >
                              Yes
                            </button>
                          </div>
                        </PayrollContainer>
                      </InputGroup>
                    )}

                    {/* Commission Type - Optional */}
                    <InputGroup>
                      <PayrollContainer>
                        <Label>Commission Type</Label>

                        <div className="flex bg-gray-100 rounded-xl p-1 w-fit">
                          <button
                            type="button"
                            onClick={() => setFieldValue("commissionType", 0)}
                            className={`px-4 py-1.5 text-sm rounded-md hover:text-gray-900 transition-all ${values.commissionType === 0
                              ? "bg-white shadow text-gray-900"
                              : "text-gray-500"
                              }`}
                          >
                            Flat
                          </button>

                          <button
                            type="button"
                            onClick={() => setFieldValue("commissionType", 1)}
                            className={`px-4 py-1.5 text-sm rounded-md hover:text-gray-900 transition-all ${values.commissionType === 1
                              ? "bg-white shadow text-gray-900"
                              : "text-gray-500"
                              }`}
                          >
                            Percentage
                          </button>
                        </div>
                      </PayrollContainer>
                    </InputGroup>

                    {/* Commission Value - Only show if commission type is selected */}
                    {values.commissionType !== null &&
                      values.commissionType !== undefined &&
                      values.commissionType !== "" && (
                        <InputGroup>
                          <Label htmlFor="commissionValue">
                            {values.commissionType === 0
                              ? "Flat Amount (₹) *"
                              : "Percentage (%) *"}
                          </Label>
                          <FieldWrappers>
                            {values.commissionType === 0 && <RupeeIcon />}
                            <Field
                              type="number"
                              name="commissionValue"
                              id="commissionValue"
                              placeholder={
                                values.commissionType === 0
                                  ? "Enter amount (max ₹5,00,000)"
                                  : "Enter % (1-100)"
                              }
                              value={values.commissionValue}
                              onChange={(e) =>
                                handleIncrementValueChange(
                                  e.target.value,
                                  setFieldValue
                                )
                              }
                              className={
                                values.commissionType === 0 ? "pl-6" : ""
                              }
                              min={values.commissionType === 0 ? "1" : "1"}
                              max={values.commissionType === 0 ? "500000" : "100"}
                            />
                            {values.commissionType === 1 && (
                              <span className="absolute right-3 text-gray-500">
                                %
                              </span>
                            )}
                          </FieldWrappers>
                          {errors.commissionValue && touched.commissionValue && (
                            <ErrorText>{errors.commissionValue}</ErrorText>
                          )}
                        </InputGroup>
                      )}
                  </Wrapper>
                </ScrollableContent>
                <ActionSection>
                  <SideCancelBtn
                    type="button"
                    onClick={() => {
                      setIsAddStaff(false);
                      resetForm();
                    }}
                  >
                    Cancel
                  </SideCancelBtn>
                  <SubmitBtn type="submit" disabled={addStaffLoading}>
                    {addStaffLoading ? "Submitting..." : "Submit"}
                  </SubmitBtn>
                </ActionSection>

                {/* Property Selection Modal */}
                <MultiPropSelectModal
                  isOpen={showPropertyModal}
                  onClose={() => setShowPropertyModal(false)}
                  onSelect={handlePropertySave}
                  options={filteredProperties}
                  selectedProperties={values.properties}
                />

                {/* Staff Role Selection Modal - Only show for non-Sales Head users */}
                {!isSalesHead && (
                  <SelectOptionModal
                    isOpen={isRoleModalOpen}
                    onClose={() => setIsRoleModalOpen(false)}
                    title="Select Staff Role"
                    options={staffRoleOptions}
                    selectedValue={values.staff}
                    onSelect={handleRoleSelect}
                    searchPlaceholder="Search staff roles..."
                  />
                )}
              </Form>
            );
          }}
        </Formik>
      </div>

    </Container>
  );

};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const Header = tw.div`flex items-center justify-between bg-primary-color py-4 px-4`;
const ErrorText = tw.div`text-red-500 text-sm ml-0.5`;
const PayrollContainer = tw.div`
  flex items-center justify-between
  border border-gray-200
  rounded-lg
  px-4 h-12
  bg-white
`;

const Toggle = ({ checked, onChange, disabled }) => (
  <ToggleLabel disabled={disabled}>
    <ToggleInput
      type="checkbox"
      checked={checked}
      onChange={onChange}
      disabled={disabled}
    />
    <ToggleSlider checked={checked} disabled={disabled}>
      <ToggleThumb checked={checked} />
    </ToggleSlider>
  </ToggleLabel>
);
const ToggleLabel = tw.label`
  ${(p) => (p.disabled ? "cursor-not-allowed" : "cursor-pointer")}
  relative inline-flex items-center
`;

const ToggleInput = tw.input`sr-only`;

const ToggleSlider = tw.div`
  relative w-12 h-6 rounded-full transition-all duration-200
  ${(p) =>
    p.disabled
      ? "bg-gray-200"
      : p.checked
        ? "bg-[#0078BD]"
        : "bg-gray-300"}
`;

const ToggleThumb = tw.div`
  absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200
  ${(p) => (p.checked ? "translate-x-6" : "")}
`;
export default AddStaff;